// Écran d'administration du Catalogue AUPRÉA — sections + filtres + édition riche
import { useState, useMemo } from "react";
import {
  C, Btn, Input, Textarea, Select, Modal, FormField, Alert, Spinner,
  useAsync, prestationsApi,
  PRESTATION_TYPES, PRESTATION_PUBLICS, PRESTATION_BASES, fmtEur } from "./shared";
import { FicheMissionModal } from "./FicheMission";

const EMPTY = {
  code: "", nom: "", description: "", quand_proposer: "",
  prix: 0, prix_affiche: "",
  base_prix: "TTC", type: "paiement_unique",
  unite: "", public: "particulier",
  ordre: 100, lien_stripe: "", actif: true,
};

const PUBLIC_BADGE = {
  particulier: { bg: C.bluelt,  fg: C.blue,  label: "Particulier" },
  dirigeant:   { bg: C.purplt,  fg: C.purp,  label: "Dirigeant"   },
  tous:        { bg: C.goldlt,  fg: C.golddm, label: "Tous"       },
};

const TYPE_BADGE = {
  paiement_unique:      { bg: C.gray2,   fg: C.mid,   label: "Paiement unique" },
  abonnement_mensuel:   { bg: C.teallt,  fg: C.teal,  label: "Abonnement" },
  orientation_gratuite: { bg: C.greenlt, fg: C.green, label: "Orientation" },
  remise:               { bg: C.amberlt, fg: C.amber, label: "Remise" },
};

const BASE_BADGE = {
  TTC: { bg: C.navylt,  fg: C.navy },
  HT:  { bg: C.purplt,  fg: C.purp },
  NA:  { bg: C.gray2,   fg: C.muted },
};

const MiniBadge = ({ style, children }) => (
  <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", ...style }}>{children}</span>
);

const PrestationForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!form.code.trim()) return setErr("Le code est requis");
    if (!form.nom.trim())  return setErr("Le nom est requis");
    setSaving(true); setErr("");
    try {
      const payload = { ...form, code: form.code.trim().toUpperCase(), prix: form.prix === "" ? null : Number(form.prix) };
      if (form.id) await prestationsApi.update(form.id, payload);
      else await prestationsApi.create(payload);
      onSaved(); onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };
  return (
    <Modal title={form.id ? `Modifier — ${form.code}` : "Nouvelle prestation"} onClose={onClose} wide
      footer={<><Btn onClick={onClose}>Annuler</Btn><Btn variant="gold" disabled={saving} onClick={submit}>{saving ? "..." : "Enregistrer"}</Btn></>}>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 14 }}>
          <FormField label="Code" required>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="Ex : CONS" data-testid="prest-code" />
          </FormField>
          <FormField label="Nom" required>
            <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex : Consultation patrimoniale initiale" data-testid="prest-nom" />
          </FormField>
        </div>
        <FormField label="Description courte">
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Phrase de vente courte…" data-testid="prest-desc" />
        </FormField>
        <FormField label="Quand la proposer ? (affiché au consultant uniquement)">
          <Textarea value={form.quand_proposer || ""} onChange={(e) => setForm({ ...form, quand_proposer: e.target.value })} placeholder="Ex : Documents dispersés, pilier 1 faible…" data-testid="prest-quand" />
        </FormField>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <FormField label="Type">
            <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="prest-type">
              {PRESTATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </FormField>
          <FormField label="Public">
            <Select value={form.public} onChange={(e) => setForm({ ...form, public: e.target.value })} data-testid="prest-public">
              {PRESTATION_PUBLICS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </Select>
          </FormField>
          <FormField label="Base de prix">
            <Select value={form.base_prix} onChange={(e) => setForm({ ...form, base_prix: e.target.value })} data-testid="prest-base">
              {PRESTATION_BASES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </Select>
          </FormField>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <FormField label="Prix (€)">
            <Input type="number" step="0.01" value={form.prix ?? ""} onChange={(e) => setForm({ ...form, prix: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="prest-prix" />
          </FormField>
          <FormField label="Prix affiché (client)">
            <Input value={form.prix_affiche} onChange={(e) => setForm({ ...form, prix_affiche: e.target.value })} placeholder="Ex : 150 € ou À partir de 500 €" data-testid="prest-prix-affiche" />
          </FormField>
          <FormField label="Unité (optionnel)">
            <Input value={form.unite} onChange={(e) => setForm({ ...form, unite: e.target.value })} placeholder="/an, /mois, /séance…" data-testid="prest-unite" />
          </FormField>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 14 }}>
          <FormField label="Ordre">
            <Input type="number" value={form.ordre} onChange={(e) => setForm({ ...form, ordre: Number(e.target.value) })} data-testid="prest-ordre" />
          </FormField>
          <FormField label="Lien Stripe Payment Link">
            <Input value={form.lien_stripe} onChange={(e) => setForm({ ...form, lien_stripe: e.target.value })} placeholder="https://buy.stripe.com/… (optionnel)" data-testid="prest-stripe" />
          </FormField>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} style={{ width: 16, height: 16 }} data-testid="prest-actif" />
          <span style={{ fontSize: 13, color: C.text }}>Prestation active (visible côté consultant)</span>
        </label>
      </div>
    </Modal>
  );
};

const SECTIONS = [
  { key: "particulier_paiement",   title: "Particuliers — Prestations",       match: (p) => p.public === "particulier" && p.type === "paiement_unique" },
  { key: "particulier_abo",        title: "Particuliers — Abonnements Carnet Digital", match: (p) => p.public === "particulier" && p.type === "abonnement_mensuel" },
  { key: "particulier_remise",     title: "Remises Particuliers",             match: (p) => p.public === "particulier" && p.type === "remise" },
  { key: "dirigeant",              title: "Dirigeants",                       match: (p) => p.public === "dirigeant"   && (p.type === "paiement_unique" || p.type === "abonnement_mensuel" || p.type === "remise") },
  { key: "orientation",            title: "Orientations gratuites",           match: (p) => p.type === "orientation_gratuite" },
];

const PrestationRow = ({ p, onEdit, onRemove, onToggleActif, onFiche }) => {
  const pubB  = PUBLIC_BADGE[p.public] || PUBLIC_BADGE.tous;
  const typB  = TYPE_BADGE[p.type]     || TYPE_BADGE.paiement_unique;
  const baseB = BASE_BADGE[p.base_prix] || BASE_BADGE.NA;
  return (
    <tr style={{ opacity: p.actif ? 1 : 0.5, borderTop: `.5px solid ${C.border}` }} data-testid={`prest-row-${p.code}`}>
      <td style={{ padding: "12px 14px", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: C.navy, whiteSpace: "nowrap" }}>{p.code}</td>
      <td style={{ padding: "12px 14px", minWidth: 220 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 3 }}>{p.nom}</div>
        <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.4 }}>{p.description || <em style={{ color: C.muted }}>—</em>}</div>
      </td>
      <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{p.prix_affiche || (p.prix != null ? `${fmtEur(p.prix)}` : "—")}</div>
        {p.base_prix !== "NA" && <MiniBadge style={{ background: baseB.bg, color: baseB.fg, marginTop: 4 }}>{p.base_prix}</MiniBadge>}
      </td>
      <td style={{ padding: "12px 14px" }}>
        <MiniBadge style={{ background: typB.bg, color: typB.fg }}>{typB.label}</MiniBadge>
      </td>
      <td style={{ padding: "12px 14px" }}>
        <MiniBadge style={{ background: pubB.bg, color: pubB.fg }}>{pubB.label}</MiniBadge>
      </td>
      <td style={{ padding: "12px 14px", textAlign: "center" }}>
        <button
          onClick={() => onToggleActif(p)}
          data-testid={`prest-toggle-${p.code}`}
          style={{ cursor: "pointer", background: p.actif ? C.greenlt : C.gray2, color: p.actif ? C.green : C.muted, border: "none", borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 600, fontFamily: "inherit" }}>
          {p.actif ? "● Active" : "○ Inactive"}
        </button>
      </td>
      <td style={{ padding: "12px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
        {["paiement_unique", "abonnement_mensuel"].includes(p.type) && <><Btn small variant={p.fiche_mission?.a_relire ? "gold" : "ghost"} onClick={() => onFiche(p)} data-testid={`prest-fiche-${p.code}`}>Fiche mission{p.fiche_mission?.a_relire ? " · à relire" : ""}</Btn>{" "}</>}
        <Btn small onClick={() => onEdit(p)} data-testid={`prest-edit-${p.code}`}>Modifier</Btn>{" "}
        <Btn small variant="red" onClick={() => onRemove(p)} data-testid={`prest-del-${p.code}`}>Suppr.</Btn>
      </td>
    </tr>
  );
};

export const PrestationsAdmin = () => {
  const { data: prestations, loading, reload } = useAsync(prestationsApi.list, []);
  const [editing, setEditing] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [fPublic, setFPublic] = useState("");
  const [fType, setFType] = useState("");
  const [fActif, setFActif] = useState("");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return (prestations || []).filter(p => {
      if (fPublic && p.public !== fPublic) return false;
      if (fType && p.type !== fType) return false;
      if (fActif === "actif" && !p.actif) return false;
      if (fActif === "inactif" && p.actif) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.code.toLowerCase().includes(q) && !p.nom.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [prestations, fPublic, fType, fActif, search]);

  if (loading) return <Spinner />;

  const remove = async (p) => {
    if (!window.confirm(`Supprimer la prestation « ${p.code} — ${p.nom} » ?`)) return;
    await prestationsApi.remove(p.id);
    reload();
  };

  const toggleActif = async (p) => {
    await prestationsApi.update(p.id, { ...p, actif: !p.actif });
    reload();
  };

  const groups = SECTIONS.map(s => ({ ...s, items: filtered.filter(s.match) })).filter(g => g.items.length);

  return (
    <>
      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
        <FormField label="Recherche">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Code ou nom…" data-testid="prest-search" />
        </FormField>
        <FormField label="Public">
          <Select value={fPublic} onChange={(e) => setFPublic(e.target.value)} data-testid="prest-filter-public">
            <option value="">Tous publics</option>
            {PRESTATION_PUBLICS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
        </FormField>
        <FormField label="Type">
          <Select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="prest-filter-type">
            <option value="">Tous types</option>
            {PRESTATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </FormField>
        <FormField label="Statut">
          <Select value={fActif} onChange={(e) => setFActif(e.target.value)} data-testid="prest-filter-actif">
            <option value="">Actifs et inactifs</option>
            <option value="actif">Actifs uniquement</option>
            <option value="inactif">Inactifs uniquement</option>
          </Select>
        </FormField>
        <Btn variant="gold" onClick={() => setEditing(EMPTY)} data-testid="prest-add">+ Nouvelle</Btn>
      </div>

      <div style={{ fontSize: 12, color: C.mid, marginBottom: 10 }}>
        {filtered.length} prestation{filtered.length > 1 ? "s" : ""} affichée{filtered.length > 1 ? "s" : ""} sur {prestations?.length || 0}
      </div>

      {groups.map(g => (
        <div key={g.key} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
          <div style={{ padding: "12px 18px", background: C.gray2, borderBottom: `.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{g.title}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{g.items.length} entrée{g.items.length > 1 ? "s" : ""}</div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#fff" }}>
                  <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em" }}>Code</th>
                  <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em" }}>Prestation</th>
                  <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em" }}>Prix</th>
                  <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em" }}>Type</th>
                  <th style={{ textAlign: "left", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em" }}>Public</th>
                  <th style={{ textAlign: "center", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em" }}>Statut</th>
                  <th style={{ textAlign: "right", padding: "10px 14px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map(p => <PrestationRow key={p.id} p={p} onEdit={setEditing} onRemove={remove} onToggleActif={toggleActif} onFiche={setFiche} />)}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {groups.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: C.muted, background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}` }}>
          Aucune prestation ne correspond aux filtres.
        </div>
      )}

      {fiche && <FicheMissionModal prestation={fiche} prestations={prestations || []} onClose={() => setFiche(null)} onSaved={reload} />}
      {editing && <PrestationForm initial={editing.id ? editing : EMPTY} onClose={() => setEditing(null)} onSaved={reload} />}
    </>
  );
};
